import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Button as MuiButton, Stack, useTheme } from '@mui/material';
import moment from 'moment';

import {
  MasterIndexApi,
  MasterNameApi,
  MasterIndexApiPath,
  AddressApi,
} from '@/api';
import { DriverLicenseApi } from '@/api/api.driver.license';
import {
  ConfirmationDialog,
  FlagChips,
  FormRoot,
  PageHeader,
} from '@/components';
import NavigationButtonGroup from '@/components/NavigationButtonGroup/NavigationButtonGroup';
import { useVerifiedFields } from '@/contexts';
import { withLoader, WithLoaderProps } from '@/hocs';
import withUnsavedChangesWarning, {
  WithUnsavedChangesWarningProps,
} from '@/hocs/withUnsavedChangesWarning';
import { useFilesValidation } from '@/hooks';
import { TabAnimationWrapper, useTabLayoutContext } from '@/layouts/TabLayout';
import {
  IKaseMasterName,
  IMasterName,
  IMasterNamePerson,
  IPhoneNumber,
} from '@/models';
import MasterIndexRelatedRecords from '@/pages/MasterIndex/components/MasterIndexRelatedRecords/MasterIndexRelatedRecords';
import MasterIndexPersonFlags from '@/pages/MasterIndex/Persons/Flags';
import { MasterIndexPersonNotes } from '@/pages/MasterIndex/Persons/Notes';
import { isValidStructuredAddress, uploadFiles } from '@/services';
import { useToastStore } from '@/store';
import { ConfirmTypes } from '@/store/store.toast';
import {
  formatPersonName,
  generateUniqueId,
  getDuplicateSocialMediaIndexes,
  getIncompleteSocialMediaFieldErrors,
  normalizeSocialMediaEntries,
  parsePhoneNumber,
} from '@/utils';

import { MasterNamePersonInformationSection } from './MasterNamePersonInformationSection';
import { TabNamesNextPaths, TabNamesScreens } from '../data';
import { useMasterNamesContext } from '../MasterNamesContext';

export type MasterNameFormData = IMasterNamePerson & {
  newPhoneNumber: IPhoneNumber;
  ssn_prev: string;
  emails?: string[] | { email: string }[];
};

type MasterNameFormComponentProps = {
  hideHeader?: boolean;
  onGoBack?: () => void;
  isMasterIndex?: boolean;
  onCustomSubmit?: (values: MasterNameFormData) => Promise<void>;
  onAfterSubmit?: (personId: string) => Promise<void>;
};

const normalizeEmails = (
  emailsValue: string[] | { email: string }[] | undefined,
): string[] => {
  if (!Array.isArray(emailsValue)) {
    return [];
  }
  if (emailsValue[0] && typeof emailsValue[0] === 'string') {
    return emailsValue as string[];
  }
  return (emailsValue as { email: string }[])
    .map((e) => e.email)
    .filter((email) => email !== '');
};

const formatDriverLicenseDate = (date?: Date | string | null) => {
  if (!date) return undefined;

  const formattedDate = moment(date);
  return formattedDate.isValid()
    ? formattedDate.format('YYYY-MM-DD')
    : undefined;
};

const getDriverLicenseErrorMessage = (error: any) => {
  const validationMessage = error?.response?.data?.errors?.[0]?.msg;
  return (
    validationMessage ||
    error?.response?.data?.message ||
    error?.message ||
    'Driver license information could not be saved.'
  );
};

export const MasterNameForm = ({
  showLoader,
  hideLoader,
  setIsDirty,
  onNavigation,
  hideHeader,
  isMasterIndex,
  onCustomSubmit,
  onAfterSubmit,
  ...props
}: WithLoaderProps &
  WithUnsavedChangesWarningProps &
  MasterNameFormComponentProps) => {
  const { updateToast } = useToastStore();
  const { kaseId } = useParams();
  const {
    currentMastername,
    setCurrentMastername,
    updateLinkedMasterNames,
    handleAddLinkedName,
    setCurrentDriverLicenseId,
  } = useMasterNamesContext();
  const { updateCurrentScreen } = useTabLayoutContext();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearErrors, handleSubmit, getValues, setError, watch } =
    useFormContext<MasterNameFormData>();

  const [showConfirm, setShowConfirm] = useState(false);

  const { isFieldVerified, getVerifiedFieldsWithValues } = useVerifiedFields();

  // State to track active tab - defaults to 'information'
  const [activeTab, setActiveTab] = useState('information');

  const uploadedFileItems = watch('files');
  const hasInvalidFiles = useFilesValidation(uploadedFileItems);
  const theme = useTheme();

  const onGoBack = () => {
    if (props.onGoBack) {
      return props.onGoBack();
    } else {
      const values = getValues();
      updateCurrentScreen(
        values._id ? TabNamesScreens.NamesList : TabNamesScreens.MasterAddName,
      );
    }

    if (searchParams.get('fieldKey')) {
      setCurrentMastername(undefined);
      navigate(`/cases/${kaseId}/nibrs`);
      return;
    }
  };

  const handleGoBack = () => {
    if (onNavigation(new Event('back'))) {
      onGoBack();
    }
  };

  const onError = () => {
    setShowConfirm(false);
  };

  const onSubmit = async (values: MasterNameFormData) => {
    if (onCustomSubmit) {
      await onCustomSubmit(values);
      onGoBack();
      return;
    }

    const incompleteSocialMediaFieldErrors =
      getIncompleteSocialMediaFieldErrors(values.socialMedia);

    if (incompleteSocialMediaFieldErrors.length > 0) {
      incompleteSocialMediaFieldErrors.forEach(
        ({ index, platformMissing, usernameMissing }) => {
          if (platformMissing) {
            setError(`socialMedia.${index}.platform`, {
              type: 'required',
              message: 'Platform is required when a username is entered.',
            });
          }

          if (usernameMissing) {
            setError(`socialMedia.${index}.username`, {
              type: 'required',
              message: 'Username is required when a platform is selected.',
            });
          }
        },
      );

      updateToast({
        open: true,
        message: 'Complete each social media entry before saving.',
        type: ConfirmTypes.ERROR,
      });
      return;
    }

    const duplicateSocialMediaIndexes = getDuplicateSocialMediaIndexes(
      values.socialMedia,
    );

    if (duplicateSocialMediaIndexes.length > 0) {
      duplicateSocialMediaIndexes.forEach((index) => {
        setError(`socialMedia.${index}.platform`, {
          type: 'duplicate',
          message: 'Duplicate social media entry.',
        });
      });

      updateToast({
        open: true,
        message: 'Duplicate social media entries are not allowed.',
        type: ConfirmTypes.ERROR,
      });
      return;
    }

    clearErrors('socialMedia');
    setIsDirty(false);

    try {
      showLoader();
      const {
        files,
        employerInfo,
        driverLicense,
        phoneNumbers,
        newPhoneNumber,
        addresses,
        socialMedia,
      } = values;

      const filteredAddresses = addresses?.filter((address) =>
        isValidStructuredAddress(address),
      );

      const filteredSocialMedia = normalizeSocialMediaEntries(socialMedia);

      if (newPhoneNumber && newPhoneNumber?.phoneNumber !== '') {
        phoneNumbers?.push(newPhoneNumber);
      }
      const parsedPhoneNumbers = phoneNumbers
        ?.filter(({ type, phoneNumber }) => type && phoneNumber !== '')
        .map((phone) => ({
          ...phone,
          phoneNumber: parsePhoneNumber(phone.phoneNumber, '+1'),
        }));

      // Dropping an empty row can remove the only primary (e.g. an added blank
      // row left unfilled); keep exactly one primary among what remains.
      if (
        parsedPhoneNumbers?.length &&
        !parsedPhoneNumbers.some((phone) => phone.isPrimary)
      ) {
        parsedPhoneNumbers[0].isPrimary = true;
      }

      const savedAddresses: typeof filteredAddresses = [];
      if (filteredAddresses && filteredAddresses.length > 0) {
        for (const address of filteredAddresses) {
          if (address._id) {
            savedAddresses.push(address);
          } else {
            try {
              const addressResponse = await AddressApi.createAddress(address);
              if (addressResponse?.data?._id) {
                savedAddresses.push({
                  ...address,
                  _id: addressResponse.data._id,
                });
              } else {
                // If the API didn't return an ID, we can't reliably reference this address.
                // Avoid console warnings (ESLint) and proceed without adding it.
                updateToast({
                  open: true,
                  message: 'Address created but no ID returned.',
                });
              }
            } catch (addressError: any) {
              updateToast({
                open: true,
                message: `Could not create address: ${addressError.message}`,
              });
            }
          }
        }
      }

      const hasDriverLicenseData =
        driverLicense &&
        typeof driverLicense === 'object' &&
        (driverLicense.number ||
          driverLicense.customerIdNumber ||
          driverLicense.documentNumber ||
          driverLicense.state ||
          driverLicense.issueDate ||
          driverLicense.expireDate ||
          driverLicense.vehicleClass);

      let driverLicensePayload;
      if (hasDriverLicenseData && driverLicense.number && driverLicense.state) {
        // Update existing license or create new one
        try {
          const addressIdForLicense = savedAddresses?.[0]?._id;

          const licenseResponse = await DriverLicenseApi.saveDriverLicense({
            // Include _id if updating existing license
            ...(driverLicense._id && { _id: driverLicense._id }),
            number: driverLicense.number,
            firstName: values.firstName || '',
            middleName: values.middleName || '',
            lastName: values.lastName || '',
            customerIdNumber: driverLicense.customerIdNumber,
            documentNumber: driverLicense.documentNumber,
            state: driverLicense.state,
            issueDate: formatDriverLicenseDate(driverLicense.issueDate),
            expireDate: formatDriverLicenseDate(driverLicense.expireDate),
            revisionDate: formatDriverLicenseDate(driverLicense.revisionDate),
            vehicleClass: driverLicense.vehicleClass,
            restrictionCodes: driverLicense.restrictionCodes,
            endorsementCodes: driverLicense.endorsementCodes,
            documentDiscriminator: driverLicense.documentDiscriminator,
            ...(addressIdForLicense && { address: addressIdForLicense }),
          });
          if (licenseResponse?.data?._id) {
            driverLicensePayload = licenseResponse.data._id;
          }
        } catch (licenseError: any) {
          throw new Error(getDriverLicenseErrorMessage(licenseError));
        }
      } else if (driverLicense?._id) {
        // No license data to update, just preserve existing reference
        driverLicensePayload = driverLicense._id;
      }

      const idScanVerifiedFields = getVerifiedFieldsWithValues();
      const payload = {
        ...values,
        address: '',
        addresses: savedAddresses,
        socialMedia: filteredSocialMedia,
        employerInfo: {
          ...employerInfo,
          address: employerInfo?.address?._id,
          phoneNumber: parsePhoneNumber(values.employerInfo?.phoneNumber, '+1'),
        },
        phoneNumbers: parsedPhoneNumbers,
        driverLicense: driverLicensePayload,
        emails: normalizeEmails(values.emails),
        emergencyContacts: values.emergencyContacts?.filter(
          (ec) => !!ec.emergencyContact,
        ),
        ...(Object.keys(idScanVerifiedFields).length > 0 && {
          idScanVerifiedFields,
        }),
      };

      if (files && files.length) {
        const uploadedFiles = await uploadFiles(files);
        if (!uploadedFiles) return;
        payload.files = uploadedFiles;
      }

      let res;
      let personId;

      if (values._id) {
        personId = values._id;

        if (isMasterIndex) {
          res = await MasterIndexApi.updateMasterPerson(payload);

          updateToast({
            open: true,
            message: 'Person updated successfully',
            type: ConfirmTypes.SUCCESS,
          });

          hideLoader();
          onGoBack();
          setShowConfirm(false);
          return;
        } else {
          res = await MasterNameApi.updateMasterName(payload);

          updateToast({
            open: true,
            message: 'Person updated successfully',
            type: ConfirmTypes.SUCCESS,
          });
        }
      } else {
        if (isMasterIndex) {
          res = await MasterIndexApi.createMasterPerson(payload);

          if (res?.data?._id) {
            personId = res.data._id;

            if (onAfterSubmit) {
              await onAfterSubmit(personId);
            }

            updateToast({
              open: true,
              message: 'Person created successfully',
              type: ConfirmTypes.SUCCESS,
            });

            hideLoader();
            onGoBack();
            setShowConfirm(false);
            return;
          }
        } else {
          res = await MasterNameApi.createMasterNamePerson(payload);

          if (res?.data?._id) {
            personId = res.data._id;

            setCurrentMastername({
              kaseId,
              relationship: {
                behavior: 'N/A',
                involvement: kaseId ? undefined : [],
              },
              masterName: res.data as IMasterNamePerson,
            } as IKaseMasterName);

            await handleAddLinkedName(personId, {
              relationship: {
                behavior: 'N/A',
                involvement: kaseId ? undefined : [],
              },
            });

            updateToast({
              open: true,
              message: 'Person created successfully',
              type: ConfirmTypes.SUCCESS,
            });
          }
        }
      }

      if (showConfirm && res?.data?._id) {
        setShowConfirm(false);

        if (res.data.type === 'PERSON') {
          return checkDriverLicenceId(res.data as IMasterNamePerson);
        }
      }

      if (!isMasterIndex) {
        onGoBack();
      }
    } catch (err: any) {
      setIsDirty(true);
      updateToast({ open: true, message: err.message });
    } finally {
      hideLoader();
      setShowConfirm(false);
    }
  };

  const checkDriverLicenceId = useCallback(
    (masterName: IMasterNamePerson) => {
      const driverLicenceID =
        masterName?.driverLicense?._id || masterName?.driverLicense;

      setCurrentDriverLicenseId(driverLicenceID?.toString() || '');

      if (!masterName._id && masterName.firstName) {
        setCurrentMastername({
          masterName,
          relationship: {
            involvement: undefined,
            behavior: 'N/A',
          },
          kaseId: kaseId || '',
          createdAt: new Date(),
          updatedAt: new Date(),
          isTemporary: true,
        } as any);
      }

      if (driverLicenceID) {
        setTimeout(
          () => updateCurrentScreen(TabNamesScreens.MasterAddDriverLicense),
          0,
        );
      } else {
        setTimeout(
          () => updateCurrentScreen(TabNamesScreens.MasterNameDriverLicense),
          0,
        );
      }
    },
    [
      setCurrentDriverLicenseId,
      updateCurrentScreen,
      setCurrentMastername,
      kaseId,
    ],
  );

  const getFullSSN = async () => {
    if (!currentMastername?.masterName?._id) return undefined;
    try {
      return await MasterNameApi.getMasterNameSSN(
        currentMastername.masterName._id,
      );
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      return undefined;
    }
  };

  const refreshMasterNameFromServer = useCallback(async () => {
    const masterNameId = currentMastername?.masterName?._id;
    if (!masterNameId) return;

    try {
      const { data } = await MasterNameApi.getMasterName(masterNameId);
      const updatedMasterName = data as IMasterName;

      // Functional updater — always spreads the LATEST currentMastername
      setCurrentMastername((prev) =>
        prev
          ? ({ ...prev, masterName: updatedMasterName } as typeof prev)
          : undefined,
      );

      // Functional updater — always maps over the LATEST linkedMasterNames
      updateLinkedMasterNames((prev) =>
        prev.map((item) =>
          item.masterName?._id === masterNameId
            ? {
                ...item,
                masterName: updatedMasterName as unknown as IMasterNamePerson,
              }
            : item,
        ),
      );
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  }, [
    currentMastername?.masterName?._id,
    setCurrentMastername,
    updateLinkedMasterNames,
    updateToast,
  ]);

  const flags =
    (currentMastername?.masterName as IMasterNamePerson)?.flags?.map(
      (f) => f.flag,
    ) ||
    (watch() as IMasterNamePerson)?.flags?.map((f) => f.flag) ||
    [];

  // Get tabs for persons
  const getTabs = () => {
    return [
      {
        label: 'Personal information',
        path: 'information',
      },
      {
        label: 'Related records',
        path: 'related',
        disabled: !currentMastername?.masterName?._id,
      },
      {
        label: 'Flags',
        path: 'flags',
        disabled: !currentMastername?.masterName?._id,
      },
      {
        label: 'Notes',
        path: 'notes',
        disabled: !currentMastername?.masterName?._id,
      },
    ];
  };

  const tabs = getTabs();

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'information':
        return (
          <>
            <FormRoot
              id={isMasterIndex ? 'master-index-form' : undefined}
              onSubmit={handleSubmit(onSubmit, onError)}
              sx={{
                pb: 0,
                '.p-kase-edit &': {
                  padding: '20px',
                  mt: 0,
                  background: theme.palette.background.paper,
                },
              }}
            >
              <MasterNamePersonInformationSection
                isMasterIndex={isMasterIndex}
                searchFieldKey={searchParams?.get('fieldKey')}
                getFullSSNApi={getFullSSN}
                isFieldVerified={isFieldVerified}
              />
            </FormRoot>
          </>
        );

      case 'related':
        return (
          <MasterIndexRelatedRecords
            type={MasterIndexApiPath.People}
            viewId={currentMastername?.masterName._id}
          />
        );

      case 'flags':
        return (
          <MasterIndexPersonFlags
            viewId={currentMastername?.masterName._id}
            onFlagsUpdate={refreshMasterNameFromServer}
          />
        );

      case 'notes':
        return (
          <MasterIndexPersonNotes
            viewId={currentMastername?.masterName._id}
            onCountChange={refreshMasterNameFromServer}
          />
        );

      default:
        return null;
    }
  };

  // Updated tabs with onClick handlers
  const tabsWithHandlers = tabs.map((tab) => ({
    ...tab,
    onClick: () => setActiveTab(tab.path),
  }));

  const getPersonTitle = () => {
    const masterName = currentMastername?.masterName;

    if (
      masterName &&
      'firstName' in masterName &&
      masterName.firstName &&
      masterName.lastName
    ) {
      if (masterName.uniqueId && !isMasterIndex) {
        return `${generateUniqueId('PER', masterName.uniqueId)} - ${
          masterName.firstName
        } ${masterName.lastName}`;
      }
      return formatPersonName(masterName);
    }

    return 'New person';
  };

  const renderTitle = () => {
    const title = getPersonTitle();

    if (flags.length > 0) {
      return (
        <Stack direction="row" alignItems="center" spacing={2}>
          <span>{title}</span>
          <FlagChips flags={flags} size="medium" animateImportant />
        </Stack>
      );
    }
    return title;
  };

  const renderActionButtons = () => {
    if (activeTab !== 'information') {
      return null;
    }

    return (
      <Stack direction="row" spacing={2}>
        <MuiButton color="inherit" variant="outlined" onClick={onGoBack}>
          Cancel
        </MuiButton>
        <MuiButton
          color="error"
          variant="contained"
          onClick={handleSubmit(onSubmit, onError)}
          disabled={hasInvalidFiles}
        >
          Save
        </MuiButton>
      </Stack>
    );
  };

  return (
    <TabAnimationWrapper nextPaths={TabNamesNextPaths.MasterAddName}>
      <div className="bg-[#F5F7F7] dark:bg-[#0B1922] flex flex-col h-full overflow-hidden text-gray-900 dark:text-gray-100">
        {!hideHeader && (
          <div className="flex-shrink-0 p-2">
            <PageHeader
              title={renderTitle()}
              onBack={handleGoBack}
              action={
                !isMasterIndex || onAfterSubmit
                  ? renderActionButtons()
                  : undefined
              }
            />
          </div>
        )}

        <div className="!w-full px-2">
          {!hideHeader && tabs && (
            <NavigationButtonGroup
              tabs={tabsWithHandlers}
              activeTab={activeTab}
            />
          )}
        </div>

        <div className="flex-1 overflow-auto p-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {renderTabContent()}
        </div>

        {!isMasterIndex && (
          <ConfirmationDialog
            title="Save User"
            description="You <b>should</b> save the <b>person record</b> before entering the <b>driver's license information</b>."
            confirmText="Save user"
            open={showConfirm}
            onCancel={() => {
              setShowConfirm(false);
            }}
            onConfirm={handleSubmit(onSubmit, onError)}
          />
        )}
      </div>
    </TabAnimationWrapper>
  );
};

export default withLoader(withUnsavedChangesWarning(MasterNameForm, false));
